import io from "socket.io-client";
import {
  setPendingFriendsInvitations,
  setFriends,
  setOnlineUsers,
} from "../store/actions/friendsActions";
import { setServerInvitations } from "../store/actions/roomActions";
import { appendMessage } from "../store/actions/chatActions";
import store from "../store/store";
import { updateDirectChatHistoryIfActive } from "../shared/utils/chat";

let socket = null;
let peerConnection = null;
let localStream = null;
let remoteAudio = null;
let activeVoicePeerId = null;
let pendingVoiceCall = null;
const pendingIceCandidates = new Map();
let currentVoiceRoomId = null;
let groupVoiceStream = null;
const groupPeerConnections = new Map();

const iceServers = {
  iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
};

const createRemoteAudio = () => {
  if (!remoteAudio) {
    remoteAudio = new Audio();
    remoteAudio.autoplay = true;
    remoteAudio.playsInline = true;
    remoteAudio.style.display = "none";
    document.body.appendChild(remoteAudio);
  }
  return remoteAudio;
};

const closeVoiceResources = () => {
  if (peerConnection) {
    peerConnection.close();
    peerConnection = null;
  }
  if (localStream) {
    localStream.getTracks().forEach((track) => track.stop());
    localStream = null;
  }
  activeVoicePeerId = null;
  pendingVoiceCall = null;
  window.dispatchEvent(new Event("voice-call-ended"));
};

const createPeerConnection = (receiverUserId) => {
  const connection = new RTCPeerConnection(iceServers);

  connection.onicecandidate = (event) => {
    if (event.candidate) {
      socket.emit("voice-ice-candidate", {
        receiverUserId,
        candidate: event.candidate,
      });
    }
  };

  connection.ontrack = (event) => {
    const audio = createRemoteAudio();
    audio.srcObject = event.streams[0];
    audio.play().catch((error) => {
      console.log("Remote audio playback was blocked", error);
    });
  };

  return connection;
};

const addQueuedIceCandidates = async (userId) => {
  const candidates = pendingIceCandidates.get(userId) || [];
  for (const candidate of candidates) {
    await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
  }
  pendingIceCandidates.delete(userId);
};

const ensureLocalStream = async () => {
  if (!localStream) {
    localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  }
  return localStream;
};

const ensureGroupVoiceStream = async () => {
  if (!groupVoiceStream) {
    groupVoiceStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  }
  return groupVoiceStream;
};

const createGroupRemoteAudio = (socketId, stream) => {
  let audio = document.getElementById(`voice-audio-${socketId}`);
  if (!audio) {
    audio = document.createElement("audio");
    audio.id = `voice-audio-${socketId}`;
    audio.autoplay = true;
    audio.playsInline = true;
    audio.style.display = "none";
    document.body.appendChild(audio);
  }
  audio.srcObject = stream;
  audio.play().catch((error) => console.log("Group voice playback blocked", error));
};

const createGroupPeerConnection = async (targetSocketId, shouldCreateOffer) => {
  if (groupPeerConnections.has(targetSocketId)) {
    return groupPeerConnections.get(targetSocketId);
  }

  const connection = new RTCPeerConnection(iceServers);
  groupPeerConnections.set(targetSocketId, connection);

  connection.onicecandidate = (event) => {
    if (event.candidate) {
      socket.emit("voice-room-ice-candidate", {
        roomId: currentVoiceRoomId,
        targetSocketId,
        candidate: event.candidate,
      });
    }
  };

  connection.ontrack = (event) => {
    createGroupRemoteAudio(targetSocketId, event.streams[0]);
  };

  const stream = await ensureGroupVoiceStream();
  stream.getTracks().forEach((track) => connection.addTrack(track, stream));

  if (shouldCreateOffer) {
    const offer = await connection.createOffer();
    await connection.setLocalDescription(offer);
    socket.emit("voice-room-offer", {
      roomId: currentVoiceRoomId,
      targetSocketId,
      offer,
    });
  }

  return connection;
};

const closeGroupVoice = () => {
  groupPeerConnections.forEach((connection, socketId) => {
    connection.close();
    const audio = document.getElementById(`voice-audio-${socketId}`);
    if (audio) {
      audio.remove();
    }
  });
  groupPeerConnections.clear();
  if (groupVoiceStream) {
    groupVoiceStream.getTracks().forEach((track) => track.stop());
    groupVoiceStream = null;
  }
  currentVoiceRoomId = null;
};

export const connectWithSocketServer = (userDetails) => {
  const jwtToken = userDetails.token;

  socket = io(process.env.REACT_APP_SOCKET_URL || "http://localhost:5001", {
    auth: {
      token: jwtToken,
    },
  });
  socket.on("connect", () => {
    console.log("Successfully connected with socket.io server");
    console.log(socket.id);
  });

  socket.on("friends-invitations", (data) => {
    const { pendingInvitations } = data;
    store.dispatch(setPendingFriendsInvitations(pendingInvitations));
  });
  socket.on("friends-list", (data) => {
    const { friends } = data;
    store.dispatch(setFriends(friends));
  });
  socket.on("server-invitations", (data) => {
    const { invitations } = data;
    store.dispatch(setServerInvitations(invitations));
    window.dispatchEvent(new Event("server-invitations-updated"));
  });
  socket.on("online-users", (data) => {
    console.log("Active user event came");
    const { onlineUsers } = data;
    store.dispatch(setOnlineUsers(onlineUsers));
  });
  socket.on("direct-chat-history", (data) => {
    console.log("Direct-chat-history by server in socket.on", data);
    updateDirectChatHistoryIfActive(data);
  });
  socket.on("channel-message", ({ serverId, channelId, message }) => {
    const chat = store.getState().chat;
    if (
      chat.chosenChatDetails?.serverId === serverId &&
      chat.chosenChatDetails?.id === channelId
    ) {
      store.dispatch(appendMessage(message));
    }
  });
  socket.on("voice-call-made", ({ fromUserId, offer }) => {
    const friends = store.getState().friends?.friends || [];
    const caller = friends.find((friend) => friend.id === fromUserId);
    pendingVoiceCall = { fromUserId, offer };
    window.dispatchEvent(
      new CustomEvent("voice-call-incoming", {
        detail: {
          fromUserId,
          callerName: caller?.username || "Пользователь",
        },
      })
    );
  });
  socket.on("voice-answer-made", async ({ fromUserId, answer }) => {
    if (peerConnection && activeVoicePeerId === fromUserId) {
      await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
      await addQueuedIceCandidates(fromUserId);
    }
  });
  socket.on("voice-ice-candidate", async ({ fromUserId, candidate }) => {
    if (!candidate) {
      return;
    }

    if (
      peerConnection &&
      activeVoicePeerId === fromUserId &&
      peerConnection.remoteDescription
    ) {
      await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
      return;
    }

    const currentCandidates = pendingIceCandidates.get(fromUserId) || [];
    pendingIceCandidates.set(fromUserId, [...currentCandidates, candidate]);
  });
  socket.on("voice-call-ended", () => {
    closeVoiceResources();
  });
  socket.on("voice-room-participants", ({ roomId, participants }) => {
    if (roomId === currentVoiceRoomId) {
      store.dispatch({
        type: "ROOM.SET_VOICE_PARTICIPANTS",
        roomId,
        participants,
      });
    }
  });
  socket.on("voice-room-user-joined", async ({ roomId, socketId }) => {
    if (roomId === currentVoiceRoomId && socketId !== socket.id) {
      await createGroupPeerConnection(socketId, true);
    }
  });
  socket.on("voice-room-user-left", ({ roomId, socketId }) => {
    if (roomId === currentVoiceRoomId && groupPeerConnections.has(socketId)) {
      groupPeerConnections.get(socketId).close();
      groupPeerConnections.delete(socketId);
      const audio = document.getElementById(`voice-audio-${socketId}`);
      if (audio) {
        audio.remove();
      }
    }
  });
  socket.on("voice-room-offer", async ({ roomId, fromSocketId, offer }) => {
    if (roomId !== currentVoiceRoomId) {
      return;
    }
    const connection = await createGroupPeerConnection(fromSocketId, false);
    await connection.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await connection.createAnswer();
    await connection.setLocalDescription(answer);
    socket.emit("voice-room-answer", {
      roomId,
      targetSocketId: fromSocketId,
      answer,
    });
  });
  socket.on("voice-room-answer", async ({ roomId, fromSocketId, answer }) => {
    if (roomId === currentVoiceRoomId && groupPeerConnections.has(fromSocketId)) {
      await groupPeerConnections
        .get(fromSocketId)
        .setRemoteDescription(new RTCSessionDescription(answer));
    }
  });
  socket.on("voice-room-ice-candidate", async ({ roomId, fromSocketId, candidate }) => {
    if (roomId === currentVoiceRoomId && groupPeerConnections.has(fromSocketId) && candidate) {
      await groupPeerConnections
        .get(fromSocketId)
        .addIceCandidate(new RTCIceCandidate(candidate));
    }
  });
};
export const sendDirectMessage = (data) => {
  socket.emit("direct-message", data);
};
export const getDirectChatHistory = (data) => {
  console.log("Direct-chat-history by server in socket.emit", data);
  socket.emit("direct-chat-history", data);
};

export const startVoiceCall = async (receiverUserId) => {
  if (!socket || !receiverUserId) {
    return;
  }

  closeVoiceResources();
  activeVoicePeerId = receiverUserId;
  peerConnection = createPeerConnection(receiverUserId);
  const stream = await ensureLocalStream();
  stream.getTracks().forEach((track) => peerConnection.addTrack(track, stream));

  const offer = await peerConnection.createOffer();
  await peerConnection.setLocalDescription(offer);

  socket.emit("voice-call-user", {
    receiverUserId,
    offer,
  });
};

export const acceptIncomingVoiceCall = async () => {
  if (!socket || !pendingVoiceCall) {
    return;
  }

  const { fromUserId, offer } = pendingVoiceCall;
  closeVoiceResources();
  activeVoicePeerId = fromUserId;
  peerConnection = createPeerConnection(fromUserId);
  const stream = await ensureLocalStream();
  stream.getTracks().forEach((track) => peerConnection.addTrack(track, stream));

  await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
  await addQueuedIceCandidates(fromUserId);
  const answer = await peerConnection.createAnswer();
  await peerConnection.setLocalDescription(answer);

  socket.emit("voice-make-answer", {
    receiverUserId: fromUserId,
    answer,
  });
};

export const rejectIncomingVoiceCall = () => {
  if (socket && pendingVoiceCall) {
    socket.emit("voice-call-end", { receiverUserId: pendingVoiceCall.fromUserId });
  }
  pendingVoiceCall = null;
  window.dispatchEvent(new Event("voice-call-ended"));
};

export const endVoiceCall = () => {
  if (socket && activeVoicePeerId) {
    socket.emit("voice-call-end", { receiverUserId: activeVoicePeerId });
  }
  closeVoiceResources();
};

export const joinVoiceRoom = async (roomId) => {
  if (!socket || !roomId) {
    return;
  }
  closeGroupVoice();
  currentVoiceRoomId = roomId;
  await ensureGroupVoiceStream();
  socket.emit("voice-room-join", { roomId });
};

export const leaveVoiceRoom = () => {
  if (socket && currentVoiceRoomId) {
    socket.emit("voice-room-leave", { roomId: currentVoiceRoomId });
  }
  closeGroupVoice();
  store.dispatch({
    type: "ROOM.SET_VOICE_PARTICIPANTS",
    roomId: null,
    participants: [],
  });
};
